import { Suspense } from "react";
import { useAuthBoot } from "../auth/useAuth.ts";
import { Skeleton } from "../ui/Skeleton.tsx";
import { ErrorBoundary } from "./ErrorBoundary.tsx";
import { useRoute } from "./router.ts";
import { NOT_FOUND, ROUTES } from "./routes.ts";
import { Toasts } from "./shell/Toasts.tsx";
import { TopBar } from "./shell/TopBar.tsx";
import s from "./shell/shell.module.css";

/** THE SHELL. It owns NO data: it resolves a route, renders the bar and the toast host, and hands the rest to a
 *  lazily-loaded screen. Every fetch, every subscription and every store write belongs to a screen or to a
 *  module a screen calls — a shell that owns data is a shell every screen waits for. */
export function App() {
  const route = useRoute();
  // The ONE auth boot for the app: restore the inert wallet record, read the auth config with backoff, verify
  // any stored bearer, probe the keystore, and install the visibility keepalive. It is idempotent and nothing
  // blocks on it — every consumer renders a pending state until it settles.
  useAuthBoot();
  const def = ROUTES.find((r) => r.label === route.label);
  const Screen = def?.component ?? NOT_FOUND;

  return (
    // ROOT boundary: the last line before a blank page. The route boundary below it is KEYED BY LABEL so that
    // navigating away from a screen that threw resets the boundary — otherwise one throw makes every later
    // route render the error state.
    <ErrorBoundary boundary="root">
      <div className={s.shell}>
        <TopBar active={route.label} />
        <main className={s.main}>
          <ErrorBoundary boundary="route" key={route.label}>
            {/* A SKELETON, never a spinner-only page and never a blank one: the first paint should state the
                layout the user is about to get. */}
            <Suspense fallback={<Skeleton variant="chart" />}>
              <Screen route={route} />
            </Suspense>
          </ErrorBoundary>
        </main>
        <Toasts />
      </div>
    </ErrorBoundary>
  );
}
